(() => {
  class ProductGridInfiniteScroll {
    constructor(container) {
      this.container = container;
      this.isLoading = false;
      this.isDone = false;
      this.observer = null;
      this.sentinel = null;
      this.loadedPageUrls = new Set();
      this.existingItemKeys = new Set();
      this.handleIntersect = this.handleIntersect.bind(this);
    }

    init() {
      if (this.container.dataset.infiniteScrollBound === 'true') return;

      this.gridRoot = this.container.querySelector('#product-grid');
      this.itemsContainer = this.resolveItemsContainer(this.gridRoot);
      this.paginationWrapper = this.container.querySelector('.pagination-wrapper');
      this.nextLink = this.getNextLink(this.paginationWrapper);
      this.sectionId = this.gridRoot?.dataset?.id;
      this.requestSectionId = this.getRequestSectionId();

      if (!this.gridRoot || !this.itemsContainer || !this.paginationWrapper || !this.nextLink || !this.requestSectionId) {
        return;
      }

      this.container.dataset.infiniteScrollBound = 'true';
      this.container.classList.add('infinite-scroll-enabled');
      this.captureExistingKeys();
      this.createSentinel();
      this.startObserver();
    }

    resolveItemsContainer(gridRoot) {
      if (!gridRoot) return null;
      if (gridRoot.tagName === 'UL') return gridRoot;
      return gridRoot.querySelector('.product-grid');
    }

    getNextLink(paginationWrapper) {
      if (!paginationWrapper) return null;
      return paginationWrapper.querySelector('a[aria-label="Next page"]');
    }

    getRequestSectionId() {
      if (window.location.pathname.startsWith('/search')) return 'main-search';
      if (window.location.pathname.includes('/collections/')) return 'main-collection-product-grid';
      return this.sectionId;
    }

    captureExistingKeys() {
      this.itemsContainer.querySelectorAll(':scope > .grid__item').forEach((item) => {
        this.existingItemKeys.add(this.getItemKey(item));
      });
    }

    createSentinel() {
      this.sentinel = document.createElement('div');
      this.sentinel.className = 'infinite-scroll-sentinel';
      this.sentinel.setAttribute('aria-live', 'polite');
      this.sentinel.innerHTML = '<span class="infinite-scroll-status">Loading more products…</span>';
      this.paginationWrapper.insertAdjacentElement('afterend', this.sentinel);
    }

    startObserver() {
      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(this.handleIntersect, {
        rootMargin: '0px 0px 500px 0px',
        threshold: 0,
      });
      this.observer.observe(this.sentinel);
    }

    async handleIntersect(entries) {
      const entry = entries[0];
      if (!entry?.isIntersecting || this.isLoading || this.isDone) return;
      await this.loadNextPage();
    }

    async loadNextPage() {
      if (!this.nextLink) {
        this.finish();
        return;
      }

      this.isLoading = true;
      this.container.classList.add('infinite-scroll-loading');
      this.setStatus('Loading more products…');

      const nextUrl = new URL(this.nextLink.href, window.location.origin);
      const pageKey = nextUrl.pathname + nextUrl.search;

      if (this.loadedPageUrls.has(pageKey)) {
        this.finish();
        return;
      }

      this.loadedPageUrls.add(pageKey);

      const requestUrl = new URL(nextUrl.pathname, window.location.origin);
      requestUrl.searchParams.set('section_id', this.requestSectionId);
      nextUrl.searchParams.forEach((value, key) => {
        requestUrl.searchParams.append(key, value);
      });

      try {
        const response = await fetch(requestUrl.toString(), {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fetchedGridRoot = doc.getElementById('product-grid');
        const fetchedItemsContainer = this.resolveItemsContainer(fetchedGridRoot);

        if (!fetchedGridRoot || !fetchedItemsContainer) {
          throw new Error('Missing fetched product grid');
        }

        const incomingItems = Array.from(fetchedItemsContainer.querySelectorAll(':scope > .grid__item'));
        const fragment = document.createDocumentFragment();
        let appendedCount = 0;

        incomingItems.forEach((item) => {
          const key = this.getItemKey(item);
          if (this.existingItemKeys.has(key)) return;
          this.existingItemKeys.add(key);
          fragment.appendChild(item.cloneNode(true));
          appendedCount += 1;
        });

        if (appendedCount > 0) {
          this.itemsContainer.appendChild(fragment);
        }

        const newPaginationWrapper = doc.querySelector('.pagination-wrapper');
        if (newPaginationWrapper) {
          this.paginationWrapper.replaceWith(newPaginationWrapper);
          this.paginationWrapper = this.container.querySelector('.pagination-wrapper');
          this.nextLink = this.getNextLink(this.paginationWrapper);
        } else {
          this.paginationWrapper?.remove();
          this.paginationWrapper = null;
          this.nextLink = null;
        }

        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(html);
        }

        if (!this.nextLink || appendedCount === 0) {
          this.finish();
        } else {
          this.setStatus('Scroll to load more');
        }
      } catch (error) {
        console.error('Infinite scroll load failed', error);
        this.container.classList.add('infinite-scroll-error');
        this.container.classList.remove('infinite-scroll-enabled');
        this.setStatus('Automatic loading paused. Use pagination below.');
        if (this.observer) this.observer.disconnect();
      } finally {
        this.isLoading = false;
        this.container.classList.remove('infinite-scroll-loading');
      }
    }

    finish() {
      this.isDone = true;
      if (this.observer) this.observer.disconnect();
      if (this.sentinel) this.sentinel.classList.add('is-complete');
      this.setStatus('No more products');
    }

    setStatus(text) {
      if (!this.sentinel) return;
      const status = this.sentinel.querySelector('.infinite-scroll-status');
      if (status) status.textContent = text;
    }

    getItemKey(item) {
      const link = item.querySelector('a.full-unstyled-link, .card-wrapper a[href], a[href]');
      if (link?.getAttribute('href')) {
        try {
          const url = new URL(link.getAttribute('href'), window.location.origin);
          return `${url.pathname}${url.search}`;
        } catch (e) {
          return link.getAttribute('href');
        }
      }
      return item.textContent.trim().slice(0, 120);
    }
  }

  const initInfiniteScroll = () => {
    document.querySelectorAll('#ProductGridContainer').forEach((container) => {
      const controller = new ProductGridInfiniteScroll(container);
      controller.init();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInfiniteScroll);
  } else {
    initInfiniteScroll();
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type !== 'childList') continue;
      if ([...mutation.addedNodes].some((node) => node.nodeType === 1 && (node.id === 'ProductGridContainer' || node.querySelector?.('#ProductGridContainer')))) {
        initInfiniteScroll();
        break;
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();
